"use client";

import Image from "next/image";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { type ColorScheme, COLOR_SCHEMES } from "@/lib/colorSchemes";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";

export interface CardCarouselItem {
  /** Circular image URL at top of card. */
  imageSrc?: string;
  imageAlt?: string;
  /** Primary text (name, title, heading). */
  heading: string;
  /** Secondary text (role, subtitle, description). */
  subheading?: string;
  /** Color scheme for this card. Defaults to "neutral-light". */
  colorScheme?: ColorScheme;
}

export interface CardCarouselProps {
  /** Section title displayed above the carousel. */
  title?: string;
  /** Section description displayed below the title. */
  description?: string;
  /** The carousel cards. */
  cards: CardCarouselItem[];
  /** Default color scheme applied to all cards. Per-card colorScheme overrides this. */
  colorScheme?: ColorScheme;
  /** When true, adds a 24px spacer after the last card to mirror the px-6 page gutter. */
  trailingGutter?: boolean;
}

function CarouselDots() {
  const { api } = useCarousel();
  const [selected, setSelected] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    const update = () => {
      setCount(api.scrollSnapList().length);
      setSelected(api.selectedScrollSnap());
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
      api.off("reInit", update);
    };
  }, [api]);

  if (count <= 1) return null;

  return (
    <div className="flex gap-2 mt-6 pr-4 md:pr-0">
      {Array.from({ length: count }).map((_, i) => (
        <Button
          key={i}
          variant="ghost"
          onClick={() => api?.scrollTo(i)}
          aria-label={`Go to slide ${i + 1}`}
          className={cn(
            "rounded-full p-0 transition-all duration-200",
            i === selected
              ? "w-12 h-3 bg-primary hover:bg-primary"
              : "w-3 h-3 bg-neutral-cool-light hover:bg-neutral-cool",
          )}
        />
      ))}
    </div>
  );
}

export function CardCarousel({
  title,
  description,
  cards,
  colorScheme: carouselScheme = "neutral-light",
  trailingGutter = false,
}: CardCarouselProps) {
  const hasMeta = !!(title || description);

  return (
    <div
      className={cn(
        hasMeta && "md:grid md:grid-cols-12 md:gap-8 md:items-start",
        "max-w-content mx-auto",
      )}
    >
      <div className={cn("min-w-0", hasMeta && "md:col-span-8")}>
        <Carousel
          opts={{ align: "start", loop: false, containScroll: "trimSnaps" }}
        >
          <CarouselContent
            className="gap-4 ml-0"
            viewportClassName={
              trailingGutter ? "[container-type:inline-size]" : undefined
            }
          >
            {cards.map((card, i) => {
              const scheme = COLOR_SCHEMES[card.colorScheme ?? carouselScheme];
              const isLast = i === cards.length - 1;
              return (
                <CarouselItem
                  key={i}
                  className="basis-[250px] pl-0"
                  style={
                    trailingGutter && isLast
                      ? { marginRight: "max(2.4rem, calc(100cqw - 51.6rem))" }
                      : undefined
                  }
                >
                  <Card
                    className={cn(
                      "border-0 shadow-none w-[250px] h-[250px] overflow-hidden",
                      scheme.bg,
                    )}
                  >
                    <CardContent className="flex flex-col p-6">
                      {card.imageSrc && (
                        <div className="relative w-24 h-24 rounded-full overflow-hidden">
                          <Image
                            src={card.imageSrc}
                            alt={card.imageAlt ?? card.heading}
                            fill
                            loading="eager"
                            className="object-cover"
                            sizes="96px"
                          />
                        </div>
                      )}
                      <p
                        className={cn(
                          "text-body-md font-semibold pt-4",
                          scheme.heading,
                        )}
                      >
                        {card.heading}
                      </p>
                      {card.subheading && (
                        <p
                          className={cn("text-body-sm mt-1", scheme.bodyMuted)}
                        >
                          {card.subheading}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselDots />
        </Carousel>
      </div>
    </div>
  );
}

export default CardCarousel;
